/**
 * Suhaani Weaves — shared storefront behaviour.
 *
 * Deliberately small and dependency free. Everything here is progressive:
 * the markup works without JavaScript, and these components layer drawers,
 * live search and quantity controls on top.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'details > summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function focusableWithin(root) {
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  function debounce(fn, wait) {
    let timer;
    return function () {
      const args = arguments;
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  let lockCount = 0;
  function lockScroll() {
    lockCount += 1;
    if (lockCount > 1) return;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.classList.add('scroll-locked');
    if (gap > 0) document.documentElement.style.setProperty('--scrollbar-gap', gap + 'px');
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount > 0) return;
    document.documentElement.classList.remove('scroll-locked');
    document.documentElement.style.removeProperty('--scrollbar-gap');
  }

  /** Announces a short message to assistive technology. */
  function announce(message) {
    const region = document.getElementById('LiveRegion');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  }

  /**
   * Fetches a rendered section from Shopify's Section Rendering API.
   * Returns the parsed document so callers can pick out the parts they need.
   */
  async function fetchSection(sectionId, params) {
    const url = new URL(window.location.pathname, window.location.origin);
    const search = new URLSearchParams(params || {});
    search.set('section_id', sectionId);
    url.search = search.toString();
    const response = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error('Section request failed: ' + response.status);
    const text = await response.text();
    return new DOMParser().parseFromString(text, 'text/html');
  }

  window.Urbanshop = window.Urbanshop || {};
  Object.assign(window.Urbanshop, {
    announce,
    debounce,
    fetchSection,
    focusableWithin,
    lockScroll,
    unlockScroll,
    routes: (window.Shopify && window.Shopify.routes) || { root: '/' },
  });

  /* ------------------------------------------------------------------ *
   * Side drawer
   *
   * One element powers the menu, search and cart panels. It traps focus,
   * closes on Escape or overlay click, and returns focus to whatever
   * opened it.
   * ------------------------------------------------------------------ */

  class SideDrawer extends HTMLElement {
    constructor() {
      super();
      this.opener = null;
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('[role="dialog"]');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
        }
      });
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.setAttribute('open', '');
      lockScroll();
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('focusin', this.onFocusIn);
      this.syncOpenerState(true);

      window.requestAnimationFrame(() => {
        const target =
          this.querySelector('[data-drawer-autofocus]') || focusableWithin(this.panel || this)[0];
        if (target) target.focus();
      });

      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('focusin', this.onFocusIn);
      this.syncOpenerState(false);

      this.restoreFocus();
      this.opener = null;
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    toggle(opener) {
      this.isOpen ? this.close() : this.open(opener);
    }

    /**
     * Returns focus to whatever opened the drawer. The opener can become
     * unfocusable while the drawer is open — the menu button, for instance, is
     * hidden at desktop widths — so focus falls back to an equivalent control
     * and finally to the main landmark rather than being dropped on <body>.
     */
    restoreFocus() {
      const candidates = [
        this.opener,
        document.querySelector('[data-drawer-open="' + this.id + '"]'),
        document.getElementById('MainContent'),
      ];

      for (const el of candidates) {
        if (!el || !document.body.contains(el)) continue;
        if (el.offsetWidth === 0 && el.offsetHeight === 0) continue;
        el.focus({ preventScroll: true });
        if (document.activeElement === el) return;
      }
    }

    /** Keeps aria-expanded on every control that targets this drawer honest. */
    syncOpenerState(expanded) {
      document
        .querySelectorAll('[data-drawer-open="' + this.id + '"][aria-expanded]')
        .forEach((el) => el.setAttribute('aria-expanded', String(expanded)));
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = focusableWithin(this.panel || this);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /** Pulls focus back if something outside the panel steals it. */
    onFocusIn(event) {
      if (!this.isOpen || !this.panel) return;
      if (this.panel.contains(event.target)) return;
      const focusable = focusableWithin(this.panel);
      if (focusable.length) focusable[0].focus();
    }
  }

  if (!customElements.get('side-drawer')) {
    customElements.define('side-drawer', SideDrawer);
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.getAttribute('data-drawer-open'));
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.toggle(opener);
  });

  /* ------------------------------------------------------------------ *
   * Header
   * ------------------------------------------------------------------ */

  function syncHeaderHeight() {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
  }

  window.addEventListener('resize', debounce(syncHeaderHeight, 150));
  document.addEventListener('DOMContentLoaded', syncHeaderHeight);
  syncHeaderHeight();

  /* Close a <details> navigation dropdown on outside click or Escape. */
  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-nav-dropdown][open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-nav-dropdown][open]').forEach((details) => {
      details.removeAttribute('open');
      const summary = details.querySelector('summary');
      if (summary && details.contains(document.activeElement)) summary.focus();
    });
  });

  /* Only one navigation dropdown open at a time. */
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!details.matches || !details.matches('[data-nav-dropdown]') || !details.open) return;
      document.querySelectorAll('[data-nav-dropdown][open]').forEach((other) => {
        if (other !== details) other.removeAttribute('open');
      });
    },
    true
  );

  /* ------------------------------------------------------------------ *
   * Quantity input
   * ------------------------------------------------------------------ */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quantity-step]');
        if (!button) return;
        event.preventDefault();
        const step = Number(button.getAttribute('data-quantity-step'));
        this.setValue(Number(this.input.value || 1) + step);
      });

      this.input.addEventListener('change', () => this.setValue(Number(this.input.value)));
      this.updateButtons();
    }

    setValue(next) {
      const min = Number(this.input.min || 1);
      const max = this.input.max === '' ? Infinity : Number(this.input.max);
      const value = Math.min(Math.max(Number.isFinite(next) ? next : min, min), max);
      if (String(value) !== this.input.value) {
        this.input.value = String(value);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.updateButtons();
    }

    updateButtons() {
      if (!this.input) return;
      const value = Number(this.input.value || 1);
      const min = Number(this.input.min || 1);
      const max = this.input.max === '' ? Infinity : Number(this.input.max);
      const minus = this.querySelector('[data-quantity-step="-1"]');
      const plus = this.querySelector('[data-quantity-step="1"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value >= max;
    }
  }

  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', QuantityInput);
  }

  /* ------------------------------------------------------------------ *
   * Copy a discount code
   * ------------------------------------------------------------------ */

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-copy-code]');
    if (!button) return;
    const code = button.getAttribute('data-copy-code');
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      button.setAttribute('data-copied', 'true');
      announce(code + ' copied');
      window.setTimeout(() => button.removeAttribute('data-copied'), 2000);
    } catch (error) {
      /**
       * Clipboard access is refused without a secure context or a user
       * gesture the browser trusts. Select the text instead so it can still
       * be copied by hand rather than failing silently.
       */
      const text = button.querySelector('[data-copy-code-text]') || button;
      const range = document.createRange();
      range.selectNodeContents(text);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  });

  /* ------------------------------------------------------------------ *
   * Predictive search
   * ------------------------------------------------------------------ */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-predictive-input]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      this.clearButton = this.querySelector('[data-predictive-clear]');
      this.form = this.querySelector('form');
      if (!this.input || !this.results) return;

      this.idleMarkup = this.results.innerHTML;
      this.activeIndex = -1;
      this.abortController = null;

      this.input.addEventListener('input', debounce(() => this.onInput(), 250));
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));

      if (this.clearButton) {
        this.clearButton.addEventListener('click', () => {
          this.input.value = '';
          this.reset();
          this.input.focus();
        });
      }

      this.closest('side-drawer')?.addEventListener('drawer:close', () => this.reset());
    }

    get terms() {
      return this.input.value.trim();
    }

    reset() {
      this.results.innerHTML = this.idleMarkup;
      this.results.removeAttribute('data-loading');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      if (this.clearButton) this.clearButton.hidden = true;
    }

    async onInput() {
      const terms = this.terms;
      if (this.clearButton) this.clearButton.hidden = terms.length === 0;

      if (terms.length < 2) {
        this.reset();
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.results.setAttribute('data-loading', 'true');

      const params = new URLSearchParams({
        q: terms,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        section_id: 'predictive-search',
      });

      const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      try {
        const response = await fetch(base + 'search/suggest?' + params.toString(), {
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error('Predictive search failed');
        const markup = await response.text();
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const section = parsed.querySelector('.predictive, .predictive__empty');
        this.results.innerHTML = section ? section.outerHTML : '';
        this.results.removeAttribute('data-loading');
        this.activeIndex = -1;
        this.decorateOptions();

        const count = section ? Number(section.getAttribute('data-predictive-count') || 0) : 0;
        this.input.setAttribute('aria-expanded', count > 0 ? 'true' : 'false');
        if (this.status) {
          this.status.textContent = count > 0 ? count + ' results' : 'No results';
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.results.removeAttribute('data-loading');
        this.results.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
      }
    }

    decorateOptions() {
      this.options = Array.from(this.results.querySelectorAll('[data-predictive-item]'));
      this.options.forEach((option, index) => {
        option.id = 'PredictiveOption-' + index;
      });
    }

    onKeydown(event) {
      if (event.key === 'Enter') return; /* let the form submit normally */
      if (!this.options || this.options.length === 0) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + delta + this.options.length) % this.options.length;
        this.highlight();
      } else if (event.key === 'Escape') {
        this.reset();
      }
    }

    highlight() {
      this.options.forEach((option, index) => {
        const active = index === this.activeIndex;
        const parent = option.closest('[role="option"]') || option;
        parent.setAttribute('aria-selected', String(active));
        if (active) {
          this.input.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView({ block: 'nearest' });
        }
      });
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }
})();
